// Fins de tâche signalées par le veilleur global (TaskEndWatcher, PLAN_SONS_NOTIFICATION.md Lot 1-bis).
// Entrée = élément `finished` de GET /notifications/tasks : { id, kind: "toolbox"|"production", status, tool, project_id }.

// Annulation demandée par l'utilisateur : `cancelled`, ou `failed` réécrit par le worker `assets` avec ce message (le
// cancel check des générateurs lève cette exception, video_gen.py l'écrit en failed) — même texte que notifications.py
export const USER_CANCEL_ERROR = "Annulé par l'utilisateur";

/** Vrai pour un Stop de l'utilisateur : ni son ni toast d'erreur. `st` = statut de tâche ({ status, error }). */
export function isUserCancel(st) {
  return st?.status === "cancelled" || String(st?.error || "").includes(USER_CANCEL_ERROR);
}

/** Clé de son, IDENTIQUE à celle des pollers de page (Toolbox `job:`, Production / Plateau / Timeline `gen:`) :
 *  un même événement ne sonne qu'une fois, qu'il soit vu par la page ou par le veilleur. */
export function taskKey(item) {
  const base = `${item.kind === "production" ? "gen" : "job"}:${item.id}`;
  return item.status === "failed" ? `${base}:failed` : base;
}

// [libellé, féminin]
const EXACT = {
  video_edit: ["Genjutsu", false],  // l'outil Edit s'appelle Genjutsu (2026-10-08)
  video_extend: ["Extension vidéo", true],
  video_upscale: ["Upscale vidéo", false],
  video_upscale_scenes: ["Upscale vidéo", false],
  video_reframe: ["Reframe", false],
  bible_explore: ["Variation", true],
  video_clean: ["Nettoyage vidéo", false],
  video_to_audio: ["Son de la vidéo", false],
  upscale: ["Upscale", false],
  image_edit: ["Retouche d'image", true],
  lip_sync: ["Lip sync", false],
  motion_control: ["Motion control", false],
  music_gen: ["Musique", true],
  sound_effects: ["Bruitage", false],
  transcribe: ["Transcription", true],
  stem_separation: ["Séparation des pistes", true],
  audio_isolation: ["Isolation de la voix", true],
  remix: ["Mixage", false],
  mix_stems: ["Mixage", false],
  make_real: ["« Rendre réel »", false],
  sheet_var: ["Planche", true],
  multishot: ["Bloc multishot", false],
};

function label(tool) {
  const t = tool || "";
  if (EXACT[t]) return EXACT[t];
  if (t.startsWith("bible_")) return ["Visuel Bible", false];
  if (t.startsWith("topaz_") || t.includes("slow_motion")) return ["Traitement Topaz", false];
  if (t.startsWith("voice")) return ["Voix", true];
  if (t.startsWith("video")) return ["Vidéo", true];
  if (t.startsWith("image")) return ["Image", true];
  return ["Génération", true];
}

/** « Vidéo terminée » · « Upscale en échec » · « 3 générations terminées · 1 en échec ». */
export function taskEndMessage(items) {
  if (items.length === 1) {
    const [name, fem] = label(items[0].tool);
    return `${name} ${items[0].status === "failed" ? "en échec" : fem ? "terminée" : "terminé"}`;
  }
  const failed = items.filter((it) => it.status === "failed").length;
  const done = items.length - failed;
  if (!done) return `${failed} générations en échec`;
  return `${done} génération${done > 1 ? "s" : ""} terminée${done > 1 ? "s" : ""}${failed ? ` · ${failed} en échec` : ""}`;
}

function viewPath(item) {
  if (item.kind === "production") return item.project_id ? `/projects/${item.project_id}/production` : null;
  return item.project_id ? `/projects/${item.project_id}/toolbox` : "/toolbox";
}

/** Page où voir le résultat (bouton « Voir »), seulement si tous les éléments mènent au même endroit. */
export function taskViewPath(items) {
  const paths = new Set(items.map(viewPath));
  return paths.size === 1 ? [...paths][0] : null;
}

// Annonces vocales (lib/voiceLines.json, Lot 3). Les ids des onglets Toolbox (jobTool de useToolboxGeneration)
// diffèrent du `tool` enregistré en base (veilleur) pour quelques outils : alias, pour qu'un même job dise la même chose.
const TOOL_ALIASES = {
  sharpen: "topaz_sharpen",
  denoise: "topaz_denoise",
  color_enhance: "topaz_color",
  video_denoise: "topaz_video_denoise",
  slow_motion: "topaz_slow_motion",
  sheet: "character_sheet",
  voice_lab: "voice_design",
};
const AUDIO_TOOLS = new Set(["audio", "voiceover", "music_gen", "sound_effects", "video_to_audio", "stem_separation",
  "audio_isolation", "remix", "mix_stems"]);
const IMAGE_TOOLS = new Set(["character_sheet", "sheet_var", "angle_change", "inpaint", "outpaint", "make_real",
  "storyboard", "bg_remove", "clean"]);
// [terminé, échec]
const VOICE_BY_CLASS = {
  multishot: ["multishot_termine", "erreur_video"],
  upscale: ["upscale_termine", "erreur_upscale"],
  audio: ["audio_termine", "erreur_audio"],
  video: ["video_terminee", "erreur_video"],
  image: ["image_terminee", "erreur_image"],
  generic: ["generation_terminee", "erreur_generation"],
};

function voiceClass(tool) {
  const t = String((Object.prototype.hasOwnProperty.call(TOOL_ALIASES, tool || "") ? TOOL_ALIASES[tool] : tool) || "");
  if (t === "multishot") return "multishot";
  if (t.includes("upscale")) return "upscale";
  if (AUDIO_TOOLS.has(t) || t.startsWith("voice")) return "audio";   // avant « video » : video_to_audio
  if (t.startsWith("video") || t.startsWith("topaz_video") || t.includes("slow_motion") || t === "lip_sync" || t === "motion_control") return "video";
  if (t.startsWith("image") || t.startsWith("bible_") || t.startsWith("topaz_") || IMAGE_TOOLS.has(t)) return "image";
  return "generic";
}

/** Phrase d'une fin de tâche selon l'outil : « Vidéo terminée » · « Erreur sur l'image » · « Génération terminée ». */
export function voiceLineFor(tool, failed) {
  return VOICE_BY_CLASS[voiceClass(tool)][failed ? 1 : 0];
}

/** Phrases d'un groupe de fins (veilleur) : une par catégorie, « Plusieurs générations… » s'il y en a plusieurs. */
export function voiceLinesFor(items) {
  const done = items.filter((it) => it.status !== "failed");
  const failed = items.filter((it) => it.status === "failed");
  return {
    done: done.length > 1 ? "generations_terminees" : done.length ? voiceLineFor(done[0].tool, false) : null,
    error: failed.length > 1 ? "erreurs_generations" : failed.length ? voiceLineFor(failed[0].tool, true) : null,
  };
}
